import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchPendingReconcile,
  linkDeliverable,
  dismissDeliverable,
} from '../services/reconcileService.js';

const pct = (n) => (n == null ? '—' : `${Math.round(n * 100)}%`);

export default function ReconcileDeliverables({ projectId, onBack, gateMode = false, onComplete }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const items = await fetchPendingReconcile(projectId);
      setPending(items);
      setError(null);
      if (gateMode && items.length === 0) onComplete?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, gateMode, onComplete]);

  useEffect(() => { load(); }, [load]);

  const removeAndMaybeComplete = (linkId) => {
    setPending((p) => {
      const next = p.filter((i) => i.linkId !== linkId);
      if (gateMode && next.length === 0) onComplete?.();
      return next;
    });
  };

  const handleLink = async (linkId, targetLinkId) => {
    setBusy((b) => ({ ...b, [linkId]: true }));
    try {
      await linkDeliverable(projectId, linkId, targetLinkId);
      removeAndMaybeComplete(linkId);
      setConfirming(null);
    } catch (e) {
      if (e.code === 'CONFLICT') { setError(e.message); await load(); }
      else setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, [linkId]: false }));
    }
  };

  const handleDismiss = async (linkId) => {
    setBusy((b) => ({ ...b, [linkId]: true }));
    try {
      await dismissDeliverable(projectId, linkId);
      removeAndMaybeComplete(linkId);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, [linkId]: false }));
    }
  };

  if (loading) return <div style={{ padding: 32, color: '#64748b' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 32 }}>
      {!gateMode && (
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, marginBottom: 16 }}
        >
          ← Back
        </button>
      )}

      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#0f172a', margin: 0 }}>Review deliverables</h1>
      <p style={{ marginTop: 8, fontSize: 14, color: '#475569', maxWidth: 640 }}>
        These uploads closely resemble documents already in this project, but not
        closely enough to link automatically. Each one must be resolved before you
        can continue — either link it as a new version of an existing document, or
        keep it as its own separate document.
      </p>

      {error && (
        <div style={{ marginTop: 16, borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 16px', fontSize: 14, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {pending.length === 0 ? (
        <div style={{ marginTop: 40, textAlign: 'center', color: '#64748b' }}>
          {gateMode ? 'All resolved — finishing up…' : 'Nothing to review.'}
        </div>
      ) : (
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {pending.map((item) => (
            <div key={item.linkId} style={{ borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontWeight: 500, color: '#0f172a' }}>{item.docName}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {item.docClass}
                    {item.discipline ? ` · ${item.discipline}` : ''}
                    {' · uploaded '}
                    {new Date(item.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  disabled={busy[item.linkId]}
                  onClick={() => handleDismiss(item.linkId)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap', opacity: busy[item.linkId] ? 0.4 : 1 }}
                >
                  Keep as separate document
                </button>
              </div>

              <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 8 }}>
                  Possible matches
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {item.candidates.map((c) => (
                    <div key={c.link_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, borderRadius: 6, background: '#f8fafc', padding: '8px 12px' }}>
                      <div style={{ fontSize: 14, color: '#334155' }}>
                        <span style={{ fontWeight: 500 }}>
                          {c.document_id?.split('-processed-').pop() || c.link_id}
                        </span>
                        <span style={{ color: '#64748b' }}>
                          {' '}· v{c.version}
                          {c.page_count ? ` · ${c.page_count} pages` : ''}
                          {c.chunk_count ? ` · ${c.chunk_count} chunks` : ''}
                        </span>
                      </div>
                      <button
                        disabled={busy[item.linkId]}
                        onClick={() => setConfirming({ item, candidate: c })}
                        style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', opacity: busy[item.linkId] ? 0.4 : 1 }}
                      >
                        Link as v{(Number(c.version) || 1) + 1}
                      </button>
                    </div>
                  ))}
                </div>
                {item.topScore != null && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
                    Content overlap with best match: {pct(item.topScore)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirming && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 10000 }}>
          <div style={{ background: '#fff', borderRadius: 8, maxWidth: 420, width: '100%', padding: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', margin: 0 }}>Link as a new version?</h2>
            <p style={{ marginTop: 12, fontSize: 14, color: '#475569' }}>
              <strong>{confirming.item.docName}</strong> will be recorded as version{' '}
              {(Number(confirming.candidate.version) || 1) + 1} of the existing
              document, which will be marked as no longer current.
            </p>
            <p style={{ marginTop: 12, fontSize: 14, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px' }}>
              Any past compliance verifications based on the previous version will
              be marked as needing re-check.
            </p>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => setConfirming(null)}
                style={{ padding: '8px 16px', fontSize: 14, color: '#475569', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleLink(confirming.item.linkId, confirming.candidate.link_id)}
                disabled={busy[confirming.item.linkId]}
                style={{ padding: '8px 16px', fontSize: 14, fontWeight: 500, color: '#fff', background: '#2563eb', borderRadius: 6, border: 'none', cursor: 'pointer', opacity: busy[confirming.item.linkId] ? 0.5 : 1 }}
              >
                Link version
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
